import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import List, {
  ListItem,
  ListItemContent,
  ListItemHeaderText,
  EmptyListItemText,
  ListItemPrimaryText,
  ListItemTertiaryText,
  TopDecorations,
  TopDecoration,
  RightDecorations,
  RightDecoration,
} from '$components/List';
import ProviderLogo from '$components/ProviderLogo';
import CustomProviderLogo from '$components/CustomProviderLogo';
import GradesReleased from '$components/GradesReleased';
import Button from '$components/Button';
import ExternalLink from '$components/ExternalLink';
import GradeLabel from '$components/GradeLabel';
import Spinner from '$components/Spinner';
import Pill from '$components/Pill';
import ManualAssignmentModal from '$components/ManualAssignmentModal';
import * as TYPES from '$variables/types';
import {
  providers,
  gradeTypeTitles,
  EDUCATOR,
  STAFF,
  STUDENT,
} from '$variables/constants';

import hasGrade from '$utils/hasGrade';
import { isInExtension, getExtensionTitle } from '$utils/extensions';
import getExternalLinkProperties from '$utils/getExternalLinkProperties';
import { HeaderItem, RightDecorationDesktop } from './Assignments.styled';

const Assignments = ({
  assignments,
  ltiProviders,
  ltiProvidersLoading,
  spaceId,
  onClick,
  assignmentsLoading,
  launchTurnitinAssignment,
  launchTurnitinSubmission,
  viewAs,
  createAssignment,
  isCreatingAssignment,
  isManualAssignmentsEnabled,
}) => {
  const [manualAssignmentModalOpen, setManualAssignmentModalOpen] =
    useState(false);
  const openManualAssignmentModal = () => setManualAssignmentModalOpen(true);
  const closeManualAssignemntModal = () => setManualAssignmentModalOpen(false);

  if (assignmentsLoading || ltiProvidersLoading) {
    return <Spinner />;
  }

  return (
    <List testId="assignments-list">
      <HeaderItem>
        <ListItemHeaderText>Assignments</ListItemHeaderText>
        {isManualAssignmentsEnabled && viewAs === EDUCATOR && (
          <Button
            onClick={openManualAssignmentModal}
            label="Add manual assignment"
            size="iconMedium"
            look="primary"
          >
            <AddIcon />
          </Button>
        )}
      </HeaderItem>
      {assignments.length === 0 && (
        <ListItem>
          <EmptyListItemText>
            Seems like there are no assignments in this space.
          </EmptyListItemText>
        </ListItem>
      )}
      {assignments.length > 0 &&
        assignments.map((assignment) => {
          const {
            endDate,
            externalId,
            gradeType,
            gradesReleasedAt,
            id,
            isUpcoming,
            provider,
            space,
            studentGrade,
            submission,
            title,
          } = assignment;
          const isManualProvider = provider === providers.MANUAL;
          const isLti1p3Provider = provider === providers.LTI_1P3;
          const isTIIAssignment = provider === providers.TURNITIN;
          const isEducator = viewAs === EDUCATOR;
          const isStudent = viewAs === STUDENT;
          const noStudentSubmission =
            isEducator && studentGrade && !studentGrade.externalId;
          const isUpcomingForStudent = isStudent && isUpcoming;
          const isStaffOrMissingSubmission =
            viewAs === STAFF || noStudentSubmission;
          const hideExternalLink =
            isUpcomingForStudent ||
            (isTIIAssignment && isStaffOrMissingSubmission);
          const showGradesReleased = provider !== providers.QUIZZES;

          const handleClick = onClick ? () => onClick(id) : undefined;

          return (
            <ListItem key={id} onClick={handleClick}>
              <ListItemContent>
                <TopDecorations>
                  <TopDecoration>
                    {!isManualProvider && !isLti1p3Provider && (
                      <ProviderLogo provider={provider} />
                    )}
                    {isManualProvider && (
                      <Pill look="grey">Manual assignment</Pill>
                    )}
                    {isLti1p3Provider && (
                      <CustomProviderLogo
                        iconUrl={ltiProviders[assignment.providerId]?.iconUrl}
                        description={
                          ltiProviders[assignment.providerId]?.description
                        }
                      />
                    )}
                  </TopDecoration>
                  {!isUpcoming && showGradesReleased && (
                    <TopDecoration>
                      <GradesReleased gradesReleasedAt={gradesReleasedAt} />
                    </TopDecoration>
                  )}
                  {!isUpcoming && gradeType && (
                    <TopDecoration>
                      <Pill data-testid="grade-type-pill" look="lightGrey">
                        {gradeTypeTitles[gradeType]}
                      </Pill>
                    </TopDecoration>
                  )}
                  {isUpcoming && (
                    <TopDecoration>
                      <Pill data-testid="upcoming-pill" look="upcoming">
                        Upcoming
                      </Pill>
                    </TopDecoration>
                  )}
                </TopDecorations>
                <ListItemPrimaryText
                  data-testid="assignment-title"
                  aria-disabled={!handleClick || isUpcomingForStudent}
                  disabled={isUpcomingForStudent}
                >
                  {title}
                </ListItemPrimaryText>
                {isEducator && isInExtension(studentGrade) ? (
                  <ListItemTertiaryText>
                    {getExtensionTitle(studentGrade)}
                  </ListItemTertiaryText>
                ) : null}
              </ListItemContent>
              <RightDecorations>
                {hasGrade(studentGrade) && (
                  <RightDecoration>
                    <GradeLabel grade={studentGrade.displayGrade} />
                  </RightDecoration>
                )}
                {!isManualProvider && !hideExternalLink && (
                  <RightDecorationDesktop>
                    <ExternalLink
                      data-testid="external-assignment-link"
                      {...getExternalLinkProperties({
                        space,
                        externalId,
                        id,
                        provider,
                        studentGrade,
                        isEducator,
                        launchTurnitinAssignment,
                        launchTurnitinSubmission,
                        submission,
                        endDate,
                      })}
                    />
                  </RightDecorationDesktop>
                )}
              </RightDecorations>
            </ListItem>
          );
        })}
      <ManualAssignmentModal
        key={manualAssignmentModalOpen}
        open={manualAssignmentModalOpen}
        onClose={closeManualAssignemntModal}
        onSave={createAssignment}
        isSaving={isCreatingAssignment}
        spaceId={spaceId}
      />
    </List>
  );
};

Assignments.propTypes = {
  spaceId: PropTypes.string.isRequired,
  assignments: PropTypes.arrayOf(TYPES.Assignment).isRequired,
  ltiProviders: PropTypes.arrayOf(TYPES.LtiProvider).isRequired,
  ltiProvidersLoading: PropTypes.bool,
  onClick: PropTypes.func,
  launchTurnitinAssignment: PropTypes.func.isRequired,
  launchTurnitinSubmission: PropTypes.func,
  assignmentsLoading: PropTypes.bool,
  viewAs: TYPES.ViewAs,
  createAssignment: PropTypes.func,
  isCreatingAssignment: PropTypes.bool,
  isManualAssignmentsEnabled: PropTypes.bool,
};

Assignments.defaultProps = {
  onClick: undefined,
  assignmentsLoading: false,
  ltiProvidersLoading: false,
  viewAs: EDUCATOR,
  isManualAssignmentsEnabled: false,
  createAssignment: () => {},
  launchTurnitinSubmission: () => {},
  isCreatingAssignment: false,
};

export default Assignments;
